import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { WillData } from './schema';
import { updatePersonInWill, removePersonFromWill, checkAllSectionsComplete } from './will-logic';

type MinimalPerson = {
  fullName?: string;
  relationship?: string;
  address?: any;
  isOver18?: boolean;
};

type DeepPartial<T> =
  T extends (infer U)[]
  ? DeepPartial<U>[]
  : T extends object
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : T;

// Helper to add a person to a list, avoiding duplicates by name
const addPersonToList = (
  list: MinimalPerson[],
  person: MinimalPerson,
  fallbackRelationship: string
) => {
  const name = person?.fullName?.trim();
  if (!name || list.some((p) => p.fullName === name)) return;

  list.push({
    fullName: name,
    relationship: person.relationship || fallbackRelationship,
    address: person.address,
    isOver18: person.isOver18,
  });
};

const collectPeopleFromData = (data: DeepPartial<WillData> | undefined): MinimalPerson[] => {
  if (!data) return [];

  const people: MinimalPerson[] = [];

  data.beneficiaries?.forEach((b: any) => addPersonToList(people, b, 'Beneficiary'));
  data.executors?.forEach((e: any) => addPersonToList(people, e, 'Executor'));
  (data as any).substituteExecutors?.forEach((e: any) => addPersonToList(people, e, 'Substitute Executor'));
  data.guardians?.forEach((g: any) => addPersonToList(people, g, g?.relationship || 'Guardian'));
  data.petGuardians?.forEach((g: any) => addPersonToList(people, g, 'Pet Guardian'));
  data.children?.forEach((c: any) => addPersonToList(people, { ...c, relationship: c?.relationship || 'Child' }, 'Child'));

  return people;
};

interface WillState {
  // Auth state
  userId: string | null;
  userEmail: string | null;
  willId: string | null;
  isGuest: boolean;
  isAuthenticated: boolean;
  pendingGiftEditIndex?: number | null;

  // Will data
  data: DeepPartial<WillData>;
  currentStep: number;
  completedSteps: number[];
  isEligible: boolean | null;
  isCapable: boolean | null;
  isSuitable: boolean | null;
  isEditingFromReview: boolean;
  navigatingBack: boolean;

  isInPaymentMode: boolean;
  forceSaveTrigger: number;

  // Mirror will state
  mirrorEnabled: boolean | null;
  mirrorPartnerEmail: string | null;
  mirrorGroupId: string | null;
  mirrorPartnerWillId: string | null;

  // Auth actions
  setAuth: (userId: string, email: string, willId: string) => void;
  setGuestAccess: (email: string, willId: string) => void;
  clearAuth: () => void;
  setWillId: (willId: string) => void;
  triggerSave: () => void;
  setPendingGiftEditIndex: (index: number | null) => void;

  // Will actions
  updatePerson: (oldFullName: string, newPerson: { fullName: string; relationship?: string; address?: any; email?: string; isOver18?: boolean }) => void;
  setData: (data: DeepPartial<WillData>) => void;
  setStep: (step: number) => void;
  setStepBack: (step: number) => void;
  markStepComplete: (step: number) => void;

  setEligibility: (eligible: boolean | null) => void;
  setCapability: (capable: boolean | null) => void;
  setSuitability: (suitable: boolean | null) => void;
  setIsEditingFromReview: (isEditing: boolean) => void;
  setIsInPaymentMode: (inPayment: boolean) => void;
  clearNavigatingBack: () => void;
  addManuallyAddedPerson: (person: { fullName: string; relationship: string; address?: any; isOver18?: boolean }) => void;
  removeManuallyAddedPerson: (fullName: string) => void;
  removePersonEverywhere: (fullName: string) => void;
  setMirrorEnabled: (enabled: boolean | null) => void;
  setMirrorPartnerEmail: (email: string) => void;
  setMirrorIds: (params: { mirrorGroupId?: string | null; mirrorPartnerWillId?: string | null }) => void;
  reset: () => void;
}

const initialState: DeepPartial<WillData> = {
  personalDetails: {
    fullName: '',
    email: '',
    dateOfBirth: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      postcode: '',
    },
  },
  executors: [],
  children: [],
  guardians: [],
  gifts: [],
  beneficiaries: [],
  exclusions: [],
  exclusionMessage: '',
  pets: [],
  petGuardians: [],
  guardianConfirmations: {},
  funeralWishes: '',
  additionalInstructions: '',
  manuallyAddedPeople: [],
};

export const useWillStore = create<WillState>()(
  persist(
    (set) => ({
      // Auth state
      userId: null,
      userEmail: null,
      willId: null,
      isGuest: false,
      isAuthenticated: false,
      pendingGiftEditIndex: null,

      // Will data
      data: initialState,
      currentStep: 0,
      completedSteps: [],
      isEligible: null,
      isCapable: null,
      isSuitable: null,
      isEditingFromReview: false,
      navigatingBack: false,

      isInPaymentMode: false,
      forceSaveTrigger: 0,
      mirrorEnabled: null,
      // Mirror fields
      mirrorPartnerEmail: null,
      mirrorGroupId: null,
      mirrorPartnerWillId: null,

      // Auth actions
      setAuth: (userId, email, willId) => set({
        userId,
        userEmail: email,
        willId,
        isGuest: false,
        isAuthenticated: true,
      }),
      setGuestAccess: (email, willId) => set({
        userId: 'guest',
        userEmail: email,
        willId,
        isGuest: true,
        isAuthenticated: false,
      }),
      clearAuth: () => set({
        userId: null,
        userEmail: null,
        willId: null,
        isGuest: false,
        isAuthenticated: false,
        pendingGiftEditIndex: null,
      }),
      setWillId: (willId) => set({ willId }),
      triggerSave: () => set((state) => ({ forceSaveTrigger: state.forceSaveTrigger + 1 })),
      setPendingGiftEditIndex: (index) => set({ pendingGiftEditIndex: index }),

      // Will actions
      updatePerson: (oldFullName, newPerson) => set((state) => ({
        data: updatePersonInWill(state.data, oldFullName, newPerson),
      })),
      setData: (newData) => set((state) => {
        const mergedData = { ...state.data, ...newData };
        const providedManual = (newData as any)?.manuallyAddedPeople;

        // If manuallyAddedPeople is explicitly provided and not empty, use it directly
        if (providedManual !== undefined && providedManual.length > 0) {
          return { data: { ...mergedData, manuallyAddedPeople: providedManual } };
        }

        // Otherwise, rebuild from existing + derived people to preserve quick-add options
        const baseManual = providedManual ?? state.data.manuallyAddedPeople ?? [];
        const derived = collectPeopleFromData(mergedData);
        const mergedManualList: MinimalPerson[] = [];

        // Merge base and derived people, avoiding duplicates
        baseManual.forEach((p: MinimalPerson) => addPersonToList(mergedManualList, p, 'Person'));
        derived.forEach((p) => addPersonToList(mergedManualList, p, 'Person'));

        return { data: { ...mergedData, manuallyAddedPeople: mergedManualList } };
      }),
      setStep: (step) => set({ currentStep: step, navigatingBack: false }),
      setStepBack: (step) => set({ currentStep: step, navigatingBack: true }),
      markStepComplete: (step) =>
        set((state) => ({
          completedSteps: [...new Set([...state.completedSteps, step])],
        })),

      setEligibility: (eligible) => set({ isEligible: eligible }),
      setCapability: (capable) => set({ isCapable: capable }),
      setSuitability: (suitable) => set({ isSuitable: suitable }),
      setIsEditingFromReview: (isEditing) => set({ isEditingFromReview: isEditing }),
      setIsInPaymentMode: (inPayment) => set({ isInPaymentMode: inPayment }),
      clearNavigatingBack: () => set({ navigatingBack: false }),
      addManuallyAddedPerson: (person) => set((state) => ({
        data: {
          ...state.data,
          manuallyAddedPeople: [...(state.data.manuallyAddedPeople || []), person],
        },
      })),
      setMirrorEnabled: (enabled) => set({ mirrorEnabled: enabled }),
      setMirrorPartnerEmail: (email) => set({ mirrorPartnerEmail: email }),
      setMirrorIds: ({ mirrorGroupId, mirrorPartnerWillId }) => set((state) => ({
        mirrorGroupId: mirrorGroupId !== undefined ? mirrorGroupId : state.mirrorGroupId,
        mirrorPartnerWillId: mirrorPartnerWillId !== undefined ? mirrorPartnerWillId : state.mirrorPartnerWillId,
      })),
      removePersonEverywhere: (fullName) => set((state) => ({
        data: removePersonFromWill(state.data, fullName),
      })),
      removeManuallyAddedPerson: (fullName) => set((state) => ({
        data: removePersonFromWill(state.data, fullName),
      })),
      reset: () => set({
        userId: null,
        userEmail: null,
        willId: null,
        isGuest: false,
        isAuthenticated: false,
        pendingGiftEditIndex: null,
        data: initialState,
        currentStep: 0,
        completedSteps: [],
        isEligible: null,
        isCapable: null,
        isSuitable: null,
        isEditingFromReview: false,
        navigatingBack: false,

        isInPaymentMode: false,
        forceSaveTrigger: 0,
        mirrorEnabled: null,
        mirrorPartnerEmail: null,
        mirrorGroupId: null,
        mirrorPartnerWillId: null,
      }),
    }),
    {
      name: 'will-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Helper function to check if all 4 core sections are complete
// Defined outside store to avoid circular reference
export const isAllSectionsComplete = (): boolean => {
  const state = useWillStore.getState();
  return checkAllSectionsComplete(state.data);
};

